import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import TireChassisStructure from 'app/shared/components/tire-chassis-structure/tire-chassis-structure';
import { ITireGroove } from 'app/shared/model/tire-groove.model';
import { convertDateTimeToPtBRWithoutHour } from 'app/shared/util/date-utils';
import React, { useEffect, useState } from 'react';
import { translate, Translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import TireVehicleOdometerField from '../component/tire-vehicle-odometer-field';
import {
  getTireTruckImplementCustomByTruckImplementId,
  getTruckImplementNumberOfTires,
} from '../tire-truckImplement-custom/tire-truck-implement-custom.reducer';
import { getTireLastOperation, saveAllTireGrooves } from './tire-groove-custom.reducer';
import { toast } from 'react-toastify';
import { BoxForm } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import { isVehicleModelTireFunctionComplete } from '../util/tire-function-completeness';

export interface ITirePositions {
  position: number;
  steppe: boolean;
  selected: boolean;
}

const TireGrooveCustomUpdate = (props: RouteComponentProps<{ truckImplementId: string }>) => {
  const dispatch = useAppDispatch();

  const [grooves, setGrooves] = useState<ITireGroove[]>([]);
  const [vehicleOdometer, setVehicleOdometer] = useState('');

  const loading = useAppSelector(state => state.tireTruckImplementCustom.loading);
  const updateSuccess = useAppSelector(state => state.tireGrooveCustom.updateSuccess);
  const tireTruckImplements = useAppSelector(state => state.tireTruckImplementCustom.entities);
  const lastOperation = useAppSelector(state => state.tireGrooveCustom.lastOperation);
  const truckImplementTireDataDTO = useAppSelector(state => state.tireTruckImplementCustom.truckImplementTireDataDTO);
  const [tirePositions, setTirePositions] = useState<ITirePositions[]>([]);

  const tireFunctionComplete = isVehicleModelTireFunctionComplete(
    truckImplementTireDataDTO?.vehicleModel,
    truckImplementTireDataDTO?.numberOfTires
  );

  useEffect(() => {
    dispatch(getTireTruckImplementCustomByTruckImplementId(props.match.params.truckImplementId));
    dispatch(getTruckImplementNumberOfTires(props.match.params.truckImplementId));
    dispatch(getTireLastOperation(Number(props.match.params.truckImplementId)));
  }, []);

  useEffect(() => {
    const data: ITireGroove[] = [];
    if (!data.length) {
      tireTruckImplements.map(tti => data.push({ tire: { id: tti?.tire?.id } }));
      setGrooves(data);
    }
  }, [tireTruckImplements]);

  const handleClose = () => {
    props.history.goBack();
  };

  useEffect(() => {
    if (updateSuccess) {
      props.history.push(`/dashboards/truck-implement/${props.match.params.truckImplementId}/tire`);
    }
  }, [updateSuccess]);

  const handleChange = (tireId: number, value) => {
    const normalizedValue = value?.replace(',', '.');

    const updatedGrooves = grooves.map(groove =>
      groove.tire?.id === tireId ? { ...groove, groove: normalizedValue } : groove
    );

    setGrooves(updatedGrooves);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!tireFunctionComplete) {
      return toast.error(translate('fleetSenseApp.tireTruckImplement.incompleteVehicleModelError'));
    }

    if (!vehicleOdometer) {
      return toast.error('Insira o odômetro do veículo');
    }

    const groovesToSave = grooves
      .filter(groove => groove.groove != null)
      .map(groove => ({
        ...groove,
        odometer: Number(vehicleOdometer),
        truckImplementId: Number(props.match.params.truckImplementId),
      }));

    if (!groovesToSave.length) {
      return toast.error('Insira a medição de sulco de pelo menos um pneu');
    }

    dispatch(saveAllTireGrooves(groovesToSave));
  };

  useEffect(() => {
    const dataPosition: ITirePositions[] = [];

    if (!dataPosition.length) {
      for (let i = 1; i <= truckImplementTireDataDTO.numberOfTires; i++) {
        dataPosition.push({ position: i, steppe: false, selected: true });
      }

      dataPosition.push({ position: dataPosition.length + 1, steppe: true, selected: true });
      dataPosition.push({ position: dataPosition.length + 1, steppe: true, selected: true });

      setTirePositions(dataPosition);
    }
  }, [truckImplementTireDataDTO]);

  const mapTireData = () => {
    return tirePositions.map(tire => {
      const tti = tireTruckImplements.find(t => t.position === tire.position);
      const groove = grooves.find(g => g.tire?.id === tti?.tire?.id);

      return {
        position: tire.position,
        steppe: tti?.steppe ? false : true,
        selected: groove?.groove ? true : false,
      };
    });
  };

  const grooveValue = tireId => {
    const value = grooves.find(g => g.tire?.id === tireId);
    return value?.groove;
  };

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: 2, p: 2 }}>
      <PageHeader
        headingId="tire-groove-measurement-heading"
        headingDataCy="tireGrooveMeasurementHeading"
        contentKey="fleetSenseApp.tireGroove.home.measurementTitle"
        showButton={false}
        onBackClick={handleClose}
      />
      {tireTruckImplements && tireTruckImplements.length > 0 ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <BoxForm onSubmit={handleSubmit}>
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle1" color="text.primary">
                        Placa <b>{tireTruckImplements[0]?.truckImplement?.licensePlate}</b>
                      </Typography>
                      <Typography variant="subtitle1" color="text.primary">
                        Chassi <b>{tireTruckImplements[0]?.truckImplement?.chassisNumber}</b>
                      </Typography>
                    </Box>
                    {lastOperation?.grooveCreated && (
                      <Typography variant="subtitle1" color="text.primary">
                        Última medição realizada: <b>{convertDateTimeToPtBRWithoutHour(lastOperation.grooveCreated)}</b>
                      </Typography>
                    )}
                  </Box>
                  {!tireFunctionComplete && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      {translate('fleetSenseApp.tireTruckImplement.incompleteVehicleModelWarning')}
                    </Alert>
                  )}
                  <TireVehicleOdometerField
                    truckImplementId={props.match.params.truckImplementId}
                    value={vehicleOdometer}
                    onChange={setVehicleOdometer}
                  />
                  <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardContent>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Pneu</TableCell>
                              <TableCell>Posição</TableCell>
                              <TableCell>Sulco (mm)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[...tireTruckImplements]
                              .sort((a, b) => a.position - b.position)
                              .map((tti, index) => (
                                <TableRow key={index}>
                                  <TableCell>{tti?.tire?.marking}</TableCell>
                                  <TableCell>
                                    <Chip label={tti.position} size="small" />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      placeholder="Sulco (mm)"
                                      name={`${tti.id}`}
                                      id={`${tti.id}`}
                                      value={grooveValue(tti.tire?.id) || ''}
                                      onChange={e => {
                                        const value = e?.target?.value;
                                        if (tti.tire?.id != null && /^\d*([,.]\d*)?$/.test(value)) {
                                          handleChange(tti.tire.id, value);
                                        }
                                      }}
                                      inputProps={{ min: 0, max: 50, inputMode: 'decimal', pattern: '\\d*([,.]\\d*)?' }}
                                      sx={{
                                        width: '70%',
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: 'transparent',
                                        },
                                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#808080',
                                        },
                                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                          borderColor: 'text.primary',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                          borderColor: 'secondary.main',
                                        },
                                        '& .MuiOutlinedInput-input:-webkit-autofill': {
                                          WebkitBoxShadow: '0 0 0 1000px transparent inset',
                                          transition: 'background-color 9999s ease-in-out 0s',
                                        },
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>

                  <RegisterButton />
                  </BoxForm>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md="auto">
              {grooves && (
                <Card>
                  <CardContent>
                    <Typography variant="h5">Chassi</Typography>
                    <TireChassisStructure
                      vehicleType={tireTruckImplements[0]?.truckImplement?.vehicleModel?.type ?? ''}
                      tireData={mapTireData()}
                    />
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </>
      ) : (
        !loading && (
          <Alert severity="warning">
            <Translate contentKey="fleetSenseApp.tire.home.notFound">No Tires found</Translate>
          </Alert>
        )
      )}
    </Box>
  );
};

export default TireGrooveCustomUpdate;
